import ArticleCard from '@features/Articles/ArticleCard';
import { ResponsiveContainer } from '@features/components/Layout/ResponsiveContainer';
import { openConfirmPrompt } from '@features/components/Prompt/globalConfirmPromptSlice';
import LogoSvg from '@icons/logo.svg?react';
import useQuestionIcon from '@pages/Question/hooks/useQuestionIcon';
import usePermissionGuard from '@shared/hooks/usePermissionGuard';
import TextCard from '@shared/ui/Card/TextCard';
import TextCardSkeleton from '@shared/ui/Card/TextCardSkeleton';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectAuthUserLanguage, selectIsAuthenticated } from '@store/reducers/authSlice';
import {
  LanguageType,
  useLazyContentsControllerGetArticlesQuery,
} from '@store/services/contentsApi';
import { useLazyQuestionScenesControllerFindAllQuery } from '@store/services/questionScenesApi';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export const HomePage = () => {
  const { t } = useTranslation();
  const lang = useAppSelector(selectAuthUserLanguage);
  const isAuth = useAppSelector(selectIsAuthenticated);
  const navigate = useNavigate();
  const { hasPermission } = usePermissionGuard();
  const dispatch = useAppDispatch();
  const { renderIcon } = useQuestionIcon();

  const [fetchArticles, { data: articlesData, isFetching }] =
    useLazyContentsControllerGetArticlesQuery();
  const [fetchQuestionScenes, { data: questionScenesData, isFetching: isFetchingQuestionScenes }] =
    useLazyQuestionScenesControllerFindAllQuery();

  const triggerFetchArticles = useCallback(() => {
    fetchArticles(
      {
        page: 1,
        pageSize: 12,
        lang: lang as LanguageType,
      },
      false,
    );
  }, [fetchArticles, lang]);

  useEffect(() => {
    triggerFetchArticles();
  }, [triggerFetchArticles]);

  useEffect(() => {
    fetchQuestionScenes();
  }, []);

  const requireLogin = useCallback(
    (options: { redirectPath?: string; fallbackPath?: string } = {}) => {
      dispatch(
        openConfirmPrompt({
          title: t('tips.require-login'),
          message: t('tips.require-login-message'),
          module: 'login',
          redirectPath: options.redirectPath ?? location.pathname,
          fallbackPath: options.fallbackPath ?? '/',
          buttons: {
            confirm: t('controls.login'),
            cancel: t('controls.cancel'),
          },
        }),
      );
    },
    [dispatch, t, location.pathname],
  );

  const renderTitle = () => {
    return (
      <div className="flex items-center gap-3 lg:hidden">
        <LogoSvg className="h-9 w-9 fill-primary" />
        <div className="flex flex-col">
          <span className="text-xl">{t('settings.app.name')}</span>
          <span className="text-[0.65rem] text-gray-500 uppercase tracking-widest">
            {t('settings.app.description')}
          </span>
        </div>
      </div>
    );
  };

  const navigatePage = (id: string, isPublic?: boolean) => {
    if (!isPublic && !isAuth) {
      requireLogin({ redirectPath: `/article/${id}` });
      return;
    }
    navigate(`/article/${id}`);
  };

  return (
    <ResponsiveContainer title={renderTitle()}>
      <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-4 lg:py-6 gap-3 mt-2 w-full px-4 pb-24">
        <div className="col-span-1 lg:col-span-4 grid grid-cols-2 gap-2">
          {!isFetchingQuestionScenes &&
            questionScenesData &&
            questionScenesData.length > 0 &&
            questionScenesData.map(scene => (
              <TextCard
                size="sm"
                key={scene.id}
                to={`/question/${scene.id}`}
                text={scene.name}
                description={scene.description}
                icon={renderIcon(scene.resultTemplate || 'default', 'fill-primary/85 w-10 h-10')}
                shine
              />
            ))}
        </div>

        {hasPermission('connections.manage') && (
          <div className="col-span-1 lg:col-span-4">
            <TextCard
              to="/connection"
              text={t('home.connectionCard.title')}
              description={t('home.connectionCard.description')}
              icon={
                <div className="icon-[solar--user-heart-line-duotone] h-14 w-14 text-primary" />
              }
            />
          </div>
        )}

        {isFetchingQuestionScenes && (
          <div className="col-span-1 lg:col-span-4">
            <TextCardSkeleton />
          </div>
        )}

        <div className="ml-2 mt-2 opacity-55 col-span-1 md:col-span-2 lg:col-span-4">
          {t('contents.tips.exploreMore')}
        </div>

        {isFetching && (
          <>
            <div className="col-span-1 md:col-span-2 lg:col-span-2">
              <ArticleCard loading />
            </div>

            <div className="col-span-1 md:col-span-2 lg:col-span-2">
              <ArticleCard loading />
            </div>
          </>
        )}

        {!isFetching &&
          articlesData?.data &&
          articlesData.data.length > 0 &&
          articlesData.data.map(article => (
            <div className="col-span-1 md:col-span-2 lg:col-span-2" key={article.id}>
              <ArticleCard
                id={article.id}
                title={article.title}
                description={article.description}
                unifiedIcon={article.icon}
                count={article.count}
                pinned={article.pinned}
                onClick={() => navigatePage(article.id, article.isPublic)}
              />
            </div>
          ))}
      </div>
    </ResponsiveContainer>
  );
};

export default HomePage;
